import { StreamConcurrencyService } from '../StreamConcurrencyService';
import {
  AnyFunc,
  createError,
  createEventEmitter,
  Error,
  ErrorCodes,
  EventEmitter,
  eventObject,
  PlatformUtil,
} from '@quickplay/foundation';
import { HeartbeatManager } from '../HeartbeatManager';
import {
  Action,
  BlackoutAction,
  BlackoutEventMetadata,
  PLAYBACK_STATES,
  Player,
  StreamTimelineEvent,
} from '@quickplay/player-interface';
import { createStreamConcurrencyServiceImpl } from '../service/StreamConcurrencyServiceImpl';
import { createHeartbeatServiceImpl } from '../service/HeatbeatServiceImpl';
import { HeartbeatService } from '../HeartbeatService';
import { HeartbeatBuilder } from '../HeartbeatBuilder';
import { HeartbeatErrorCodes } from '../HeartbeatErrorCodes';
import { OverflowEventMode } from '../OverflowMode';
import { HeartbeatAction, HeartbeatResponse } from '../fl-heartbeat';
import { Live360Detector } from './Live360Detector';
import { DefaultLive360Detector } from './DefaultLive360Detector';
import { Live360DetectorListenerImpl } from './Live360DetectorListenerImpl';
import { BoundaryChecker } from './model/BoundaryChecker';
import { BoundaryCheckerImpl } from './BoundaryCheckerImpl';
import { BoundaryListenerImpl } from './BoundaryListenerImpl';
import { ProgramInfo } from './model/ProgramInfo';
import { getEpochTime } from './HeartbeatUtils';

export function createHeartbeatManager(builder: HeartbeatBuilder): HeartbeatManager {
  let interval: any;
  let numberOfHeartbeatFailures = 0;
  let playbackPaused = false;
  let liveEventStartReported = false;
  let liveEndReported = false;
  let pgmId: string | undefined;
  let custom: { [p: string]: any } | undefined;

  const logger = builder.logger;
  const eventEmitter: EventEmitter = createEventEmitter();
  const configuration = builder.configuration;
  const heartbeatSyncIntervalInMS = configuration.syncIntervalMS || 60000;
  const maxAllowedFailures = configuration.maxAllowedFailures || 2;
  const stopPlayerOnStreamTimelineEvent = configuration.stopPlayerOnStreamTimelineEvent;
  const showPostEventSlateTimeMs = configuration.showPostEventSlateTimeMs || 900000;
  let internalStreamConcurrencyService = builder.streamConcurrencyService;
  let internalHeartbeatService = builder.heartbeatService;
  let heartbeatToken = builder.heartbeatToken;
  let liveStartTime: string | undefined = builder.liveStartTime;
  let liveEndTime: string | undefined = builder.liveEndTime;
  let playbackLiveStartTime: number = getEpochTime(liveStartTime);
  let playbackLiveEndTime: number = getEpochTime(liveEndTime);
  const postEventEndTime: number = getPostEventEndTime(liveEndTime);
  let overflowEventMode: OverflowEventMode | undefined = builder.overflowEventMode;
  let multiStreamMode: boolean | undefined = configuration.multiStreamMode;
  const liveEventType: string | undefined = builder.liveEventType;
  let streamDeletePromise: Promise<void> | undefined = undefined;
  const live360StartTime: string | undefined = builder.live360StartTime;
  const live360EndTime: string | undefined = builder.live360EndTime;
  let live360Detector: Live360Detector | undefined = undefined;
  let programBoundaryChecker: BoundaryChecker | undefined = undefined;
  let pgmStartTime: string | undefined = builder.pgmStartTime;
  let pgmEndTime: string | undefined = builder.pgmEndTime;
  let pgmBlackout: boolean | undefined = builder.pgmBlackout;

  const HEARTBEAT_ABORTED = 'Heartbeat-aborted';

  const heartbeatManager = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.unsubscribe(eventName, callback);
    },

    get streamConcurrencyService(): StreamConcurrencyService | undefined {
      if (!internalStreamConcurrencyService && configuration.streamConcurrencyEndPointUrl != undefined) {
        internalStreamConcurrencyService = createStreamConcurrencyServiceImpl(
          configuration.streamConcurrencyEndPointUrl,
          builder.platformAuthorizer,
          builder.configuration,
        );
      }
      return internalStreamConcurrencyService;
    },

    get heartbeatService(): HeartbeatService {
      if (!internalHeartbeatService) {
        internalHeartbeatService = createHeartbeatServiceImpl(
          builder.contentId,
          builder.deviceId,
          configuration.heartbeatEndPointUrl,
          builder.platformAuthorizer,
          builder.headers,
        );
      }

      return internalHeartbeatService;
    },

    processPlayerStateChange(player: Player): void {
      switch (player.playerState) {
        case PLAYBACK_STATES.LOADED: {
          if (!live360Detector && live360StartTime && live360EndTime) {
            live360Detector = new DefaultLive360Detector(
              getEpochTime(live360StartTime),
              getEpochTime(live360EndTime),
              new Live360DetectorListenerImpl(player),
            );
          }

          break;
        }
        case PLAYBACK_STATES.STARTED: {
          // Note: CBT-7756 - Earlier, this initProgramBoundaryChecker was implemented with player state `LOADED`.
          // This was not working for Chromecast, because the Chromecast `flcastplayer` does not have the LOADED state.
          // That's why we moved this to player state `STARTED`.
          initProgramBoundaryChecker(player);
          if (playbackPaused) {
            putStream(player);
            playbackPaused = false;
          }
          startTimer(player);
          break;
        }
        case PLAYBACK_STATES.PAUSED:
        case PLAYBACK_STATES.STOPPED:
        case PLAYBACK_STATES.IDLE: {
          playbackPaused = true;
          if (multiStreamMode !== true) {
            // do not remove stream when in multi-stream mode
            // to avoid aborting other streams on the same device
            deleteStream();
          }
          stopTimer();
          break;
        }
      }
    },

    processHeartbeatChange(player: Player): void {
      if (!player.isLive) {
        return;
      }

      live360Detector?.setCurrentPlayhead(player.currentEpochTime);
      programBoundaryChecker?.playheadChange(player.currentEpochTime);

      if (isPlayingPreEvent() && hasEventStarted(player) && !liveEventStartReported) {
        liveEventStartReported = true;
        player.send(StreamTimelineEvent.LIVE_EVENT_START, Action.START_PLAYBACK);
        stopPlayer(player);
      } else if (!isPlayingPostEvent() && hasEnded(player) && !liveEndReported) {
        liveEndReported = true;
        const event = isPlayingEvent() ? StreamTimelineEvent.LIVE_EVENT_END : StreamTimelineEvent.LIVE_PROGRAM_END;
        player.send(event, event === StreamTimelineEvent.LIVE_PROGRAM_END ? Action.START_PLAYBACK : Action.NONE);
        stopPlayer(player);
      } else if (isPlayingPostEvent() && hasPostEventEnded(player)) {
        player.send(StreamTimelineEvent.OVERFLOW_EVENT_END, Action.NONE);
        stopPlayer(player);
      }
    },

    setOverflowEventMode(mode: OverflowEventMode): void {
      overflowEventMode = mode;
    },

    setMultiStreamMode(enabled: boolean): void {
      multiStreamMode = enabled;
    },

    updateHeartbeatToken(token: string): void {
      heartbeatToken = token;
    },
  };

  function isPlayingPreEvent(): boolean {
    if (!liveEventType) {
      return false;
    }

    return overflowEventMode === OverflowEventMode.PRE_EVENT;
  }

  function isPlayingPostEvent(): boolean {
    if (!liveEventType) {
      return false;
    }

    return overflowEventMode === OverflowEventMode.POST_EVENT;
  }

  function isPlayingEvent(): boolean {
    if (!liveEventType) {
      return false;
    }

    return overflowEventMode === OverflowEventMode.EVENT || overflowEventMode === undefined;
  }

  function hasEventStarted(player: Player): boolean {
    if (!playbackLiveStartTime || player.currentEpochTime <= 0) {
      return false;
    }

    // Note: We should switch to the live event as soon as it starts and not wait
    // until the playhead crosses the event start time
    return player.currentEpochTime + player.currentOffsetFromLiveEdge() >= playbackLiveStartTime;
  }

  function hasPostEventEnded(player: Player): boolean {
    if (!postEventEndTime || player.currentEpochTime <= 0) {
      return false;
    }
    return player.currentEpochTime >= postEventEndTime;
  }

  function hasEnded(player: Player): boolean {
    if (!playbackLiveEndTime || player.currentEpochTime <= 0) {
      return false;
    }

    return player.currentEpochTime >= playbackLiveEndTime;
  }

  function stopPlayer(player: Player): void {
    if (stopPlayerOnStreamTimelineEvent !== false) {
      player.stop();
    }
  }

  function shouldSendOffset(player: Player): boolean {
    // record for VOD contents and when opted-in
    return !player.isLive && configuration.recordBookmark === true;
  }

  function blackoutError(blackoutAction: BlackoutAction): Error {
    const errorCode =
      blackoutAction === BlackoutAction.ALLOW_WITH_UPGRADE
        ? HeartbeatErrorCodes.BLACKOUT_ALLOW_UPGRADE_FAILURE
        : HeartbeatErrorCodes.BLACKOUT_DENY_FAILURE;
    return createError(errorCode | ErrorCodes.ERROR_CATEGORY_MASK_SERVER, 'Blacked-out');
  }

  function getHeartbeatActionErrorCode(heartbeatAction: HeartbeatAction): any {
    switch (heartbeatAction) {
      case HeartbeatAction.ABORT_ROAMING:
        return HeartbeatErrorCodes.HEARBEAT_ABORT_ROAMING;
      case HeartbeatAction.ABORT_NOT_ENTITLED:
        return HeartbeatErrorCodes.HEARBEAT_ABORT_NOT_ENTITLED;
      case HeartbeatAction.ABORT_BLACKOUT:
        return HeartbeatErrorCodes.HEARBEAT_ABORT_BLACKOUT;
    }
  }

  function processHeartbeatAction(heartbeatAction: HeartbeatAction, player: Player): void {
    switch (heartbeatAction) {
      case HeartbeatAction.ABORT_BLACKOUT:
      case HeartbeatAction.ABORT_NOT_ENTITLED:
      case HeartbeatAction.ABORT_ROAMING:
        eventEmitter.publish('heartbeataction', heartbeatAction);
        player.abort(
          createError(
            getHeartbeatActionErrorCode(heartbeatAction) | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
            HEARTBEAT_ABORTED,
          ),
        );
        stopTimer();
        break;
      case HeartbeatAction.CONTINUE:
      case HeartbeatAction.GRACEFUL_STOP:
        eventEmitter.publish('heartbeataction', heartbeatAction);
        break;
    }
  }

  function processBlackoutAction(heartbeatResponse: HeartbeatResponse, player: Player): void {
    switch (heartbeatResponse.blackoutAction) {
      case BlackoutAction.DENY:
      case BlackoutAction.ALLOW_WITH_UPGRADE:
        const blackoutEventMetadata: BlackoutEventMetadata = {
          blackoutUrl: heartbeatResponse.blackoutUrl,
          blackoutAction: heartbeatResponse.blackoutAction,
        };
        eventEmitter.publish('blackout', blackoutEventMetadata);
        player.send(StreamTimelineEvent.BLACKOUT, Action.NONE, blackoutEventMetadata);
        player.abort(blackoutError(heartbeatResponse.blackoutAction));
        stopTimer();
        break;
    }
  }

  function initProgramBoundaryChecker(player: Player): void {
    // To intialize the program boundry checker.
    if (programBoundaryChecker) {
      return;
    }

    const listener = new BoundaryListenerImpl(player, onProgramStart);

    let currentProgramInfo: ProgramInfo | undefined = undefined;
    if (pgmStartTime && pgmEndTime) {
      currentProgramInfo = {
        pgmId,
        pgmStartTime,
        pgmEndTime,
        pgmBlackout: pgmBlackout ?? false,
        custom,
      };

      programBoundaryChecker = new BoundaryCheckerImpl(listener, currentProgramInfo);
    }
  }

  function processHeartbeatResponse(heartbeatResponse: HeartbeatResponse, player: Player): void {
    if (heartbeatResponse.heartbeatToken) {
      heartbeatToken = heartbeatResponse.heartbeatToken;
    }

    if (heartbeatResponse.liveEndTime) {
      liveEndTime = heartbeatResponse.liveEndTime;
      playbackLiveEndTime = getEpochTime(liveEndTime);
    }

    if (heartbeatResponse.liveStartTime) {
      liveStartTime = heartbeatResponse.liveStartTime;
      playbackLiveStartTime = getEpochTime(liveStartTime);
    }

    if (heartbeatResponse.heartbeatAction) {
      processHeartbeatAction(heartbeatResponse.heartbeatAction, player);
    }

    if (heartbeatResponse.blackoutAction) {
      processBlackoutAction(heartbeatResponse, player);
    }

    if (heartbeatResponse.live360StartTime && live360Detector) {
      live360Detector.setStartEpochTime(getEpochTime(heartbeatResponse.live360StartTime));
    }

    if (heartbeatResponse.live360EndTime && live360Detector) {
      live360Detector.setEndEpochTime(getEpochTime(heartbeatResponse.live360EndTime));
    }

    if (heartbeatResponse.pgmStartTime && heartbeatResponse.pgmEndTime && programBoundaryChecker) {
      pgmId = heartbeatResponse.pgmId;
      pgmStartTime = heartbeatResponse.pgmStartTime;
      pgmEndTime = heartbeatResponse.pgmEndTime;
      pgmBlackout = heartbeatResponse.pgmBlackout ?? false;

      programBoundaryChecker?.updateProgramTime({
        pgmId,
        pgmStartTime,
        pgmEndTime,
        pgmBlackout: pgmBlackout ?? false,
        custom: heartbeatResponse.custom,
      });
    }
  }

  function isNetworkFailureOnPS4(error: Error): boolean {
    const isNetworkError = error?.doesBelongToCategory?.(ErrorCodes.ERROR_CATEGORY_MASK_NETWORK);
    return PlatformUtil.isPS4() && !!isNetworkError;
  }

  async function heartbeat(player: Player): Promise<void> {
    if (!heartbeatToken) {
      return;
    }

    try {
      const offset = shouldSendOffset(player) ? player.currentTime : undefined;
      const response = await heartbeatManager.heartbeatService.heartbeat(
        heartbeatToken,
        offset,
        builder.catalogType,
        builder.primaryContentId,
      );

      if (!response) {
        return;
      }

      processHeartbeatResponse(response, player);
      numberOfHeartbeatFailures = 0;
    } catch (error) {
      // Note: FLFoundation's network stack seems to think fetch calls are timing out
      // while in reality the calls are actually successful. This is observed on quiet a
      // sizable number of PS4 devices and only in certain cases (not reproducible in FL sample app).
      // Since this is affecting a lot of users, we are ignoring timeout calls only on PS4 platform.
      // See FLPS-33 for more details
      if (!isNetworkFailureOnPS4(error as Error)) {
        numberOfHeartbeatFailures++;
      } else {
        logger.warn('Ignoring Heartbeat ping time-out on PlayStation 4 platform');
      }

      const isServerError =
        error &&
        (error as Error).doesBelongToCategory &&
        (error as Error).doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER);
      if (isServerError || numberOfHeartbeatFailures >= maxAllowedFailures) {
        player.abort(error as Error);
        stopTimer();
      }
    }
  }

  function putStream(player: Player): void {
    const perform = (): void => {
      if (heartbeatManager.streamConcurrencyService === undefined) {
        return;
      }

      heartbeatManager.streamConcurrencyService
        .putStreamForDeviceId(
          builder.deviceId,
          builder.contentId,
          builder.catalogType,
          builder.primaryContentId,
          builder.heartbeatToken,
        )
        .catch((error: Error) => {
          player.abort(error);
          stopTimer();
        });
    };

    // Wait until stream DELETE is complete before firing the PUT
    if (!streamDeletePromise) {
      perform();
      return;
    }

    streamDeletePromise.finally(() => {
      perform();
    });
  }

  function deleteStream(): void {
    if (heartbeatManager.streamConcurrencyService === undefined) {
      return;
    }
    streamDeletePromise = heartbeatManager.streamConcurrencyService
      .deleteStreamForDeviceId(builder.deviceId)
      .catch((_error: Error) => {
        // do nothing
      });
  }

  function startTimer(player: Player): void {
    if (interval) return;
    interval = setInterval(async () => {
      await heartbeat(player);
    }, heartbeatSyncIntervalInMS);
  }

  function stopTimer(): void {
    clearInterval(interval);
    interval = undefined;
  }

  function getPostEventEndTime(isoString?: string): number {
    if (!isoString) {
      return 0;
    }
    const liveEventEndTime = new Date(isoString);
    return new Date(liveEventEndTime.getTime() + showPostEventSlateTimeMs).valueOf() / 1000;
  }

  function onProgramStart(player: Player, metadata?: ProgramInfo): void {
    player.send(StreamTimelineEvent.LIVE_PROGRAM_START, Action.NONE, metadata);
  }

  return heartbeatManager;
}
